/**
 * Smart Data Grid Component
 * 
 * Advanced data grid component with:
 * - Column customization
 * - Sorting and filtering
 * - Quick filter chips
 * - Export functionality (CSV, Excel, PDF)
 * - Row selection
 * - Pagination
 * - Search functionality
 * - Keyboard shortcuts support
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.columns - Column definitions
 * @param {Array} props.data - Data rows
 * @param {Function} [props.onRowClick] - Callback when row is clicked
 * @param {Function} [props.onSelectionChange] - Callback when selection changes
 * @param {boolean} [props.selectable=false] - Whether rows are selectable
 * @param {boolean} [props.sortable=true] - Whether columns are sortable
 * @param {boolean} [props.filterable=true] - Whether filtering is enabled
 * @param {boolean} [props.exportable=true] - Whether export is enabled
 * @param {Function} [props.onExport] - Export callback with format param
 * @param {Array} [props.quickFilters] - Quick filter definitions [{key, label, value, color}]
 * @param {string} [props.activeQuickFilter] - Currently active quick filter key
 * @param {Function} [props.onQuickFilterChange] - Callback when quick filter changes
 * @param {Array} [props.rowsPerPageOptions=[10, 25, 50, 100]] - Rows per page options
 * @param {number} [props.defaultRowsPerPage=25] - Default rows per page
 * @param {boolean} [props.loading=false] - Loading state
 * @returns {JSX.Element} The smart data grid component
 */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ViewColumn as ColumnIcon,
  Download as ExportIcon,
  MoreVert as MoreIcon,
  FileDownload as CSVIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PDFIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const SmartDataGrid = ({
  columns = [],
  data = [],
  onRowClick,
  onSelectionChange,
  selectable = false,
  sortable = true,
  filterable = true,
  exportable = true,
  onExport,
  quickFilters = [],
  activeQuickFilter = null,
  onQuickFilterChange,
  rowsPerPageOptions = [10, 25, 50, 100],
  defaultRowsPerPage = 25,
  _loading = false
}) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  // Ensure columns is always an array before calling reduce
  const safeColumns = Array.isArray(columns) ? columns : [];
  const [visibleColumns, setVisibleColumns] = useState(
    safeColumns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {})
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = filteredAndSortedData.map((row) => row.id);
      setSelected(newSelected);
      if (onSelectionChange) onSelectionChange(newSelected);
    } else {
      setSelected([]);
      if (onSelectionChange) onSelectionChange([]);
    }
  };

  const handleSelectRow = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
    if (onSelectionChange) onSelectionChange(newSelected);
  };

  const toggleColumn = (columnId) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    // Ensure data is always an array
    let filtered = Array.isArray(data) ? data : [];

    // Apply search filter
    if (searchTerm && filtered.length > 0) {
      filtered = filtered.filter(row =>
        safeColumns.some(col => {
          const value = row[col.id];
          return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply sorting
    if (orderBy) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];

        if (aVal < bVal) return order === 'asc' ? -1 : 1;
        if (aVal > bVal) return order === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [data, searchTerm, orderBy, order, safeColumns]);

  const paginatedData = useMemo(() => {
    return filteredAndSortedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredAndSortedData, page, rowsPerPage]);

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const handleExport = (format) => {
    setExportMenuAnchor(null);
    if (onExport) {
      onExport(format, filteredAndSortedData);
    }
  };

  return (
    <Box>
      {/* Quick Filters Row */}
      {quickFilters.length > 0 && (
        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
          <Chip
            label={t("common.all", "All")}
            variant={activeQuickFilter === null ? "filled" : "outlined"}
            color={activeQuickFilter === null ? "primary" : "default"}
            onClick={() => onQuickFilterChange && onQuickFilterChange(null)}
            size="small"
          />
          {quickFilters.map((filter) => (
            <Chip
              key={filter.key}
              label={filter.label}
              variant={activeQuickFilter === filter.key ? "filled" : "outlined"}
              color={activeQuickFilter === filter.key ? (filter.color || "primary") : "default"}
              onClick={() => onQuickFilterChange && onQuickFilterChange(filter.key)}
              size="small"
              icon={filter.icon}
            />
          ))}
          {activeQuickFilter && (
            <Chip
              label={t('common.clearFilters', 'Clear filters')}
              icon={<ClearIcon />}
              variant="outlined"
              color="error"
              onClick={() => onQuickFilterChange && onQuickFilterChange(null)}
              size="small"
              sx={{ ml: 1 }}
            />
          )}
        </Stack>
      )}

      {/* Toolbar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        {filterable && (
          <TextField
            size="small"
            placeholder={t('common.search', 'Search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ width: 300 }}
          />
        )}

        <Box display="flex" gap={1} alignItems="center">
          {selected.length > 0 && (
            <Chip
              label={`${selected.length} selected(s)`}
              onDelete={() => {
                setSelected([]);
                if (onSelectionChange) onSelectionChange([]);
              }}
              color="primary"
            />
          )}

          <Tooltip title={t('common.columns', 'Columns')}>
            <IconButton
              size="small"
              onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
            >
              <ColumnIcon />
            </IconButton>
          </Tooltip>

          {exportable && (
            <>
              <Tooltip title={t('common.export', 'Export')}>
                <IconButton size="small" onClick={(e) => setExportMenuAnchor(e.currentTarget)}>
                  <ExportIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
              >
                <MenuItem onClick={() => handleExport('csv')}>
                  <ListItemIcon><CSVIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('common.exportCSV', 'Export CSV')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleExport('excel')}>
                  <ListItemIcon><ExcelIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('common.exportExcel', 'Export Excel')}</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => handleExport('pdf')}>
                  <ListItemIcon><PDFIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('common.exportPDF', 'Export PDF')}</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Column visibility menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
      >
        {safeColumns.map(col => (
          <MenuItem key={col.id} onClick={() => toggleColumn(col.id)}>
            <Checkbox checked={visibleColumns[col.id]} size="small" />
            {col.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > filteredAndSortedData.length}
                    checked={filteredAndSortedData.length > 0 && selected.length === filteredAndSortedData.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {safeColumns.filter(col => visibleColumns[col.id]).map((column) => (
                <TableCell key={column.id}>
                  {sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => {
              const isItemSelected = isSelected(row.id);
              return (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => onRowClick && onRowClick(row)}
                  selected={isItemSelected}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectRow(row.id);
                        }}
                      />
                    </TableCell>
                  )}
                  {safeColumns.filter(col => visibleColumns[col.id]).map((column) => (
                    <TableCell key={column.id}>
                      {column.render ? column.render(row[column.id], row) : row[column.id]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={filteredAndSortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage={t('common.rowsPerPage', 'Rows per page:')}
          labelDisplayedRows={({ from, to, count }) => t('common.paginationRange', '{{from}}-{{to}} of {{count}}', { from, to, count })}
        />
      </TableContainer>
    </Box>
  );
};

export default React.memo(SmartDataGrid);

